import {
    UiComponentDataContext,
    UiComponentRendererPlugin,
    UiComponentRenderingContext,
    UiComponentRenderingProps,
    UiComponentStateContext,
    UiComponentValidationContext
} from "../UiComponentRendererPlugin";
import {ReactElement} from "react";
import {AttributeDefinitionDto} from "../../../../../../common/apis/stereotype/AttributeDefinitionDto";
import {ElementDto} from "../../../../../../common/apis/element/ElementDto";
import {UiComponent} from "../model/UiComponent";
import {t} from "../../../../../../store/localization/TranslMessasge";
import {NumberField} from "../model/NumberField";
import {ValuePersister} from "./ValuePersister";
import {PropertyValuePersister} from "./PropertyValuePersister";
import elementService from "../../../elementdetail/service/ElementService";
import {NumericField} from "../../../../../../components/fields/textfield/NumericField";
import {CoreDataType} from "../../../../../../common/apis/datatype/DataTypeDefinitionDto";
import {InputAdornment} from "@mui/material";
import ErrorFieldPlaceholder from "./ErrorFieldPlaceholder";
import {StereotypesTranslationKey} from "../../StereotypesTranslationKey";

export class NumberFieldPlugin implements UiComponentRendererPlugin {

    private readonly valuePersister: ValuePersister = new PropertyValuePersister(elementService);

    getSupportedComponentTypes(): string[] {
        return ["NumberField"];
    }

    getInitialValues(component: UiComponent, dataContext: UiComponentDataContext, renderingContext: UiComponentRenderingContext): Promise<Record<string, any>> {
        const attributeDefinitionId = component.attributeDefinitionId;
        const value = this.getPropertyValue(dataContext.element, dataContext.attributeDefinitions[component.attributeDefinitionId]);
        if (value) {
            return Promise.resolve({
                [attributeDefinitionId]: value
            });
        } else {
            return Promise.resolve({});
        }
    }

    private getPropertyValue(element: ElementDto, attributeDefinition?: AttributeDefinitionDto): string | undefined {
        const property = element.properties?.find(prop => prop.definition.name === attributeDefinition?.name);
        if (property) {
            return property.value;
        } else {
            return undefined;
        }
    }

    validate(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext, validationContext: UiComponentValidationContext): Record<string, string> {
        const numberField = component as NumberField;

        const attributeDefinition: AttributeDefinitionDto | undefined = dataContext.attributeDefinitions[numberField.attributeDefinitionId];
        if (!attributeDefinition) {
            return {};
        }

        let value = stateContext.values[numberField.attributeDefinitionId];
        if (value !== undefined && value !== null) {
            value = value.toString();
        }

        return validationContext.valueValidator.validateValue(value, attributeDefinition, t);
    }

    renderComponent({component, dataContext, stateContext, renderingContext}: UiComponentRenderingProps): ReactElement {
        const numberField = component as NumberField;

        const attributeDefinition = dataContext.attributeDefinitions[numberField.attributeDefinitionId];
        if (!attributeDefinition) {
            return (
                    <ErrorFieldPlaceholder key={numberField.id} error={t(StereotypesTranslationKey.UNKNOWN_ATTRIBUTE)} />
            );
        }

        const value = stateContext.values[numberField.attributeDefinitionId];
        const error = stateContext.errors[numberField.attributeDefinitionId];
        const readOnly = numberField.readOnly || !stateContext.editMode;

        function onChange(newValue?: number) {
            stateContext.onValuesChange({[numberField.attributeDefinitionId]: newValue?.toString()});
        }

        let inputAdornment = null;
        if (attributeDefinition?.dataType?.coreDataType === CoreDataType.CURRENCY && attributeDefinition?.additionalProperties?.currencyCode) {
            inputAdornment = (<InputAdornment position="start">{attributeDefinition.additionalProperties.currencyCode}</InputAdornment>);
        }

        return (
                <NumericField
                        key={numberField.id}
                        label={attributeDefinition.name}
                        value={value ?? ""}
                        onChange={onChange}
                        placeholder={numberField.placeholder}
                        helperText={numberField.helperText}
                        errorMessage={error ? t(error) : undefined}
                        required={attributeDefinition.required}
                        precision={2}
                        allowedInputRegex={/^-?\d+([.,]\d+)?$/}
                        InputProps={{
                            startAdornment: inputAdornment,
                            readOnly: readOnly,
                        }}
                        fullWidth
                />
        );
    }

    save(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext): Promise<void>[] {
        const numberField = component as NumberField;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[numberField.attributeDefinitionId];
        if (!attributeDefinition) {
            return [];
        }

        const value = stateContext.values[numberField.attributeDefinitionId];
        return [this.valuePersister.updateValue(dataContext.element, attributeDefinition, value)];
    }
}